// MessagesUnreadContext — shared unread-state signal for the Hub.
//
// Gives the nav badge, the Home banner, and the Messages page a single shared
// signal. The count is seeded from the backend timeline (a trainer message newer
// than the pet parent's last-read watermark), so the badge/banner reflect real
// unread on any Hub page. Opening Messages (the divider entering the viewport) calls
// markAllRead(), which persists the read watermark via mutation and optimistically
// clears the badge. We deliberately do NOT refetch the timeline here: re-fetching
// mid-view would clear the per-message unread flags and make the divider vanish under
// the user. The message flags clear naturally on the next page mount.
import React, {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { GET_HUB_UNREAD_COUNT, MARK_HUB_MESSAGES_READ } from '../../../queries';

interface MessagesUnreadValue {
  unreadCount: number;
  markAllRead: (upToSentAt?: string) => void;
}

const MessagesUnreadContext = createContext<MessagesUnreadValue | null>(null);

export function MessagesUnreadProvider({ children }: { children: React.ReactNode }) {
  const { data } = useQuery<{ hubMessageTimeline?: { unreadCount: number } }>(
    GET_HUB_UNREAD_COUNT,
    { fetchPolicy: 'cache-and-network' },
  );
  const [markRead] = useMutation(MARK_HUB_MESSAGES_READ);
  const [unreadCount, setUnreadCount] = useState(0);

  // Seed/refresh from the backend. markAllRead's optimistic 0 holds until the next
  // fetch returns a (now 0, or freshly higher) count.
  const backendCount = data?.hubMessageTimeline?.unreadCount;
  useEffect(() => {
    if (typeof backendCount === 'number') setUnreadCount(backendCount);
  }, [backendCount]);

  const value = useMemo(
    () => ({
      unreadCount,
      markAllRead: (upToSentAt?: string) => {
        setUnreadCount(0); // optimistic: clear badge + banner immediately
        markRead({ variables: { upToSentAt } }).catch(() => {});
      },
    }),
    [unreadCount, markRead],
  );

  return (
    <MessagesUnreadContext.Provider value={value}>
      {children}
    </MessagesUnreadContext.Provider>
  );
}

export function useMessagesUnread(): MessagesUnreadValue {
  const ctx = useContext(MessagesUnreadContext);
  // Tolerate being read outside the provider (returns a no-op) so consumers
  // don't crash if mounted in isolation.
  return ctx ?? { unreadCount: 0, markAllRead: () => {} };
}

export default MessagesUnreadContext;
